import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Network, Search, ChevronRight, Info, ShieldAlert,
  ArrowDownLeft, ArrowDownRight, X, RefreshCw, List, GitBranch, Plus, Minus,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const pts = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const TXT = {
  fr: {
    root: "RACINE", roots: "Racines", descendantsOf: (c) => `Descendants de ${c}`, noDescendant: "Aucun descendant",
    matchedPoints: "Points appariés", bonus5: "Bonus (5%)", matchedTimes5: "matched × 5%",
    ledgerWrite: "Écriture ledger", availableWord: "Disponible", calcChain: "Chaîne de calcul",
    ce1: "Commandes éligibles → volume conservé propagé aux branches",
    ceVol: (l, r) => `Volume cumulé: LEFT ${l} · RIGHT ${r}`,
    ceMatch: (m) => `Matching = min(L, R) = ${m}`,
    ceBonus: (m, b) => `Bonus = ${m} × 5% = ${b}`,
    ceLedger: (t, s) => `Ledger (${t}) → ${s}`,
    retainedValue: "Valeur conservée", orderNum: (n) => `Commande #${n || "—"}`,
    pointsAssigned: "Points attribués", branch: (b) => `Branche ${b}`,
    sourceToAncestor: "Membre source → Ancêtre", relBranchForAncestor: "Branche relative pour cet ancêtre:",
    binaryPath: "Chemin binaire (branche relative à chaque ancêtre)",
    whyBonus: "Pourquoi ce bonus ?", whyVolume: "Pourquoi ce volume ?",
    colType: "Type", colAmount: "Montant", colPoints: "Points", colStatus: "Statut", why: "Pourquoi ?",
    noBonus: "Aucun bonus.", colOrder: "Commande", colBranch: "Branche", colDepth: "Prof.",
    noVolumeMove: "Aucun mouvement de volume.",
    assumedSide: "côté supposé (backfill)", sponsor: (c) => `sponsor: ${c}`,
    volLeft: "Volume LEFT", volRight: "Volume RIGHT", carryLeft: "Carry LEFT", carryRight: "Carry RIGHT",
    matching: "Matching", leftConsumed: (v) => `LEFT consommé: ${v}`, rightConsumed: (v) => `RIGHT consommé: ${v}`,
    carryCreated: (v, s) => `Carry créé: ${v} (${s})`, bonusTarget5: "Bonus cible (5%):",
    bonusPending: "Bonus en attente", bonusAvailable: "Bonus disponible", totalGenerated: "Total généré",
    binaryBonuses: "Bonus binaires", volumePropagation: "Propagation de volume (audit)",
    volumeEvents: "Événements de volume (order_id)", noResult: "Aucun résultat.",
    reduce: "Réduire", expand: "Développer", assumedSideShort: "Côté supposé (backfill)",
    rootLabel: "Racine :", noNode: "Aucun nœud à afficher.",
    rootNoChild: "Ce membre est une racine sans filleul placé — aucun descendant à afficher.",
    pageTitle: "Traçabilité Binaire", pageSub: "Lecture seule — vérifiez chaque calcul (volumes, matching, carry-over, bonus 5%).",
    refresh: "Actualiser", list: "Liste", graphTree: "Arbre graphique",
    searchPh: "Rechercher: member_code, user_id, order_id…", searchBtn: "Rechercher",
    graphTitle: "Arbre Binaire — vue graphique (lecture seule)", expandHint: "+/- pour développer · clic pour les détails",
    noRoot: "Aucune racine.", clickNode: "Cliquez sur un nœud pour voir ses volumes, matching et bonus.",
    loading: "Chargement…", searchResults: "Résultats de recherche", treeRoots: "Arbre Binaire (racines)",
    selectMember: "Sélectionnez un membre pour voir ses volumes, matching et bonus.",
  },
  en: {
    root: "ROOT", roots: "Roots", descendantsOf: (c) => `Descendants of ${c}`, noDescendant: "No descendant",
    matchedPoints: "Matched points", bonus5: "Bonus (5%)", matchedTimes5: "matched × 5%",
    ledgerWrite: "Ledger entry", availableWord: "Available", calcChain: "Calculation chain",
    ce1: "Eligible orders → retained volume propagated to branches",
    ceVol: (l, r) => `Accumulated volume: LEFT ${l} · RIGHT ${r}`,
    ceMatch: (m) => `Matching = min(L, R) = ${m}`,
    ceBonus: (m, b) => `Bonus = ${m} × 5% = ${b}`,
    ceLedger: (t, s) => `Ledger (${t}) → ${s}`,
    retainedValue: "Retained value", orderNum: (n) => `Order #${n || "—"}`,
    pointsAssigned: "Points assigned", branch: (b) => `Branch ${b}`,
    sourceToAncestor: "Source member → Ancestor", relBranchForAncestor: "Relative branch for this ancestor:",
    binaryPath: "Binary path (relative branch at each ancestor)",
    whyBonus: "Why this bonus?", whyVolume: "Why this volume?",
    colType: "Type", colAmount: "Amount", colPoints: "Points", colStatus: "Status", why: "Why?",
    noBonus: "No bonus.", colOrder: "Order", colBranch: "Branch", colDepth: "Depth",
    noVolumeMove: "No volume movement.",
    assumedSide: "assumed side (backfill)", sponsor: (c) => `sponsor: ${c}`,
    volLeft: "LEFT volume", volRight: "RIGHT volume", carryLeft: "LEFT carry", carryRight: "RIGHT carry",
    matching: "Matching", leftConsumed: (v) => `LEFT consumed: ${v}`, rightConsumed: (v) => `RIGHT consumed: ${v}`,
    carryCreated: (v, s) => `Carry created: ${v} (${s})`, bonusTarget5: "Target bonus (5%):",
    bonusPending: "Pending bonus", bonusAvailable: "Available bonus", totalGenerated: "Total generated",
    binaryBonuses: "Binary bonuses", volumePropagation: "Volume propagation (audit)",
    volumeEvents: "Volume events (order_id)", noResult: "No result.",
    reduce: "Collapse", expand: "Expand", assumedSideShort: "Assumed side (backfill)",
    rootLabel: "Root:", noNode: "No node to display.",
    rootNoChild: "This member is a root with no placed referral — no descendant to display.",
    pageTitle: "Binary Traceability", pageSub: "Read-only — verify every calculation (volumes, matching, carry-over, 5% bonus).",
    refresh: "Refresh", list: "List", graphTree: "Graphical tree",
    searchPh: "Search: member_code, user_id, order_id…", searchBtn: "Search",
    graphTitle: "Binary tree — graphical view (read-only)", expandHint: "+/- to expand · click for details",
    noRoot: "No root.", clickNode: "Click a node to see its volumes, matching and bonus.",
    loading: "Loading…", searchResults: "Search results", treeRoots: "Binary tree (roots)",
    selectMember: "Select a member to see their volumes, matching and bonus.",
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

const RISK_COLORS = {
  OK: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  REVIEW: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  SUSPENDED: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

function Chip({ children, className = "", title }) {
  return <span title={title} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${className}`}>{children}</span>;
}

function SideBadge({ side, L }) {
  if (side === "LEFT") return <Chip className="border-sky-500/30 bg-sky-500/10 text-sky-400"><ArrowDownLeft className="h-3 w-3" />LEFT</Chip>;
  if (side === "RIGHT") return <Chip className="border-violet-500/30 bg-violet-500/10 text-violet-400"><ArrowDownRight className="h-3 w-3" />RIGHT</Chip>;
  return <Chip className="border-slate-600 bg-slate-700/30 text-slate-400">{L?.root || "ROOT"}</Chip>;
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-bold text-slate-100">{value}</div>
      {hint ? <div className="text-[11px] text-slate-500">{hint}</div> : null}
    </div>
  );
}

function NodeRow({ node, onSelect, active, L }) {
  const assumed = node.placement_side_assumed;
  return (
    <button onClick={() => onSelect(node)} data-testid={`binary-tree-node-${node.member_id}`}
      className={`flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${active ? "bg-blue-600/20 ring-1 ring-blue-500/40" : "hover:bg-slate-800/60"}`}>
      <SideBadge side={node.placement_side} L={L} />
      <span className="truncate font-mono text-xs text-slate-200">{node.member_code}</span>
      <Chip className="border-slate-600 bg-slate-800 text-slate-400">{`G${node.generation ?? "—"}`}</Chip>
      {assumed && <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-400" title={L.assumedSideShort}>backfill</Chip>}
      {node.risk_state !== "OK" && <Chip className={RISK_COLORS[node.risk_state]}>{node.risk_state}</Chip>}
      {node.child_count > 0 && <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-slate-500" />}
      <span className="shrink-0 text-[11px] text-slate-500">{`L ${pts(node.left_volume)} · R ${pts(node.right_volume)}`}</span>
    </button>
  );
}

function TreeExplorer({ roots, selectedId, onSelect, L }) {
  const [cols, setCols] = useState([]);
  useEffect(() => { setCols([{ label: L.roots, nodes: roots }]); }, [roots, L.roots]);

  const open = async (node, colIdx) => {
    onSelect(node.member_id);
    if (!node.child_count) { setCols((p) => p.slice(0, colIdx + 1)); return; }
    try {
      const res = await api.get(`/admin/binary/tree/children/${node.member_id}`);
      const ch = res.data.children || {};
      const nodes = [ch.left, ch.right].filter(Boolean);
      setCols((p) => [...p.slice(0, colIdx + 1), { label: node.member_code, nodes }]);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="max-h-[560px] space-y-2 overflow-auto">
      {cols.map((col, idx) => (
        <div key={idx} style={{ marginLeft: idx * 12 }} className={idx ? "border-l border-slate-800 pl-2" : ""}>
          <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-600">{idx === 0 ? L.roots : L.descendantsOf(col.label)}</div>
          <div className="space-y-0.5">
            {col.nodes.map((n) => <NodeRow key={n.member_id} node={n} active={selectedId === n.member_id} onSelect={(nd) => open(nd, idx)} L={L} />)}
            {!col.nodes.length && <div className="px-2 py-1 text-xs text-slate-600">{L.noDescendant}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function BonusExplain({ p, L }) {
  return (
    <div className="space-y-3 text-sm text-slate-300">
      <div className="grid grid-cols-2 gap-2">
        <Stat label={L.matchedPoints} value={pts(p.matching?.matched)} hint={`min(L ${pts(p.matching?.left)}, R ${pts(p.matching?.right)})`} />
        <Stat label={L.bonus5} value={money(p.bonus_calc?.expected_total_bonus)} hint={L.matchedTimes5} />
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
        <div className="text-[11px] uppercase text-slate-500">{L.ledgerWrite}</div>
        <div className="mt-1 font-mono text-xs">{`${p.ledger_entry?.entry_type} · ${money(p.ledger_entry?.amount)} · ${p.ledger_entry?.status}`}</div>
        {p.ledger_entry?.available_at ? <div className="text-[11px] text-slate-500">{`${L.availableWord}: ${p.ledger_entry.available_at}`}</div> : null}
      </div>
      <div className="text-[11px] uppercase text-slate-500">{L.calcChain}</div>
      <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-400">
        <li>{L.ce1}</li>
        <li>{L.ceVol(pts(p.accumulated_volume?.left_total), pts(p.accumulated_volume?.right_total))}</li>
        <li>{L.ceMatch(pts(p.matching?.matched))}</li>
        <li>{L.ceBonus(pts(p.matching?.matched), money(p.bonus_calc?.expected_total_bonus))}</li>
        <li>{L.ceLedger(p.ledger_entry?.entry_type, p.ledger_entry?.status)}</li>
      </ol>
    </div>
  );
}

function VolumeExplain({ p, L }) {
  const path = p.binary_path || [];
  return (
    <div className="space-y-3 text-sm text-slate-300">
      <div className="grid grid-cols-2 gap-2">
        <Stat label={L.retainedValue} value={money(p.retained_value)} hint={L.orderNum(p.order?.order_number)} />
        <Stat label={L.pointsAssigned} value={pts(p.points)} hint={L.branch(p.relative_branch_for_this_ancestor)} />
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
        <div className="text-[11px] uppercase text-slate-500">{L.sourceToAncestor}</div>
        <div className="mt-1 font-mono text-xs">{`${p.source_member?.member_code} → ${p.ancestor_member?.member_code}`}</div>
        <div className="mt-1 flex items-center gap-2">{L.relBranchForAncestor} <SideBadge side={p.relative_branch_for_this_ancestor} L={L} /></div>
      </div>
      <div className="text-[11px] uppercase text-slate-500">{L.binaryPath}</div>
      <div className="space-y-1">
        {path.map((x, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950/40 px-2 py-1 text-xs">
            <span className="text-slate-500">{`#${x.depth}`}</span>
            <span className="font-mono text-slate-300">{x.ancestor_member_code}</span>
            <SideBadge side={x.relative_branch} L={L} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ExplainModal({ explain, onClose, L }) {
  if (!explain) return null;
  const isBonus = explain.kind === "bonus";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="binary-explain-modal" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-100"><Info className="h-4 w-4 text-blue-400" />{isBonus ? L.whyBonus : L.whyVolume}</h3>
          <button onClick={onClose} data-testid="binary-explain-close"><X className="h-5 w-5 text-slate-400 hover:text-white" /></button>
        </div>
        {isBonus ? <BonusExplain p={explain.payload} L={L} /> : <VolumeExplain p={explain.payload} L={L} />}
      </div>
    </div>
  );
}

function BonusTable({ bonuses, onWhy, L }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-800">
      <table className="w-full text-xs">
        <thead className="bg-slate-800/50 text-slate-400"><tr>
          <th className="px-2 py-1.5 text-left">{L.colType}</th><th className="px-2 py-1.5 text-right">{L.colAmount}</th>
          <th className="px-2 py-1.5 text-right">{L.colPoints}</th><th className="px-2 py-1.5 text-left">{L.colStatus}</th><th className="px-2 py-1.5"></th>
        </tr></thead>
        <tbody>
          {bonuses.map((b) => (
            <tr key={b.ledger_id} className="border-t border-slate-800" data-testid={`binary-bonus-row-${b.ledger_id}`}>
              <td className="px-2 py-1.5 font-mono text-[11px] text-slate-300">{b.entry_type === "BINARY_REVERSAL" ? "REVERSAL" : "BONUS"}</td>
              <td className={`px-2 py-1.5 text-right ${b.amount < 0 ? "text-rose-400" : "text-emerald-400"}`}>{money(b.amount)}</td>
              <td className="px-2 py-1.5 text-right text-slate-400">{pts(b.matched_points)}</td>
              <td className="px-2 py-1.5 text-slate-300">{b.status}</td>
              <td className="px-2 py-1.5 text-right">
                <button onClick={() => onWhy(b.ledger_id)} data-testid={`binary-why-bonus-${b.ledger_id}`} className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"><Info className="h-3.5 w-3.5" /> {L.why}</button>
              </td>
            </tr>
          ))}
          {!bonuses.length ? <tr><td colSpan={5} className="px-2 py-3 text-center text-slate-600">{L.noBonus}</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function EventTable({ events, onWhy, L }) {
  return (
    <div className="max-h-52 overflow-auto rounded-lg border border-slate-800">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-slate-800/50 text-slate-400"><tr>
          <th className="px-2 py-1.5 text-left">{L.colOrder}</th><th className="px-2 py-1.5 text-left">{L.colBranch}</th>
          <th className="px-2 py-1.5 text-right">{L.colPoints}</th><th className="px-2 py-1.5 text-right">{L.colDepth}</th><th className="px-2 py-1.5"></th>
        </tr></thead>
        <tbody>
          {events.map((ev) => (
            <tr key={ev.id} className="border-t border-slate-800" data-testid={`binary-event-row-${ev.id}`}>
              <td className="px-2 py-1.5 font-mono text-[11px]">{`#${ev.order_number}`}</td>
              <td className="px-2 py-1.5"><SideBadge side={ev.branch} L={L} /></td>
              <td className={`px-2 py-1.5 text-right ${ev.points < 0 ? "text-rose-400" : "text-slate-300"}`}>{pts(ev.points)}</td>
              <td className="px-2 py-1.5 text-right text-slate-500">{ev.placement_depth}</td>
              <td className="px-2 py-1.5 text-right">
                <button onClick={() => onWhy(ev.id)} data-testid={`binary-why-volume-${ev.id}`} className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"><Info className="h-3.5 w-3.5" /> {L.why}</button>
              </td>
            </tr>
          ))}
          {!events.length ? <tr><td colSpan={5} className="px-2 py-3 text-center text-slate-600">{L.noVolumeMove}</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function MemberDetail({ detail, bonuses, events, onWhyBonus, onWhyVolume, L }) {
  const m = detail.member;
  const riskCls = RISK_COLORS[m.risk_state];
  return (
    <div className="space-y-4" data-testid="binary-member-detail">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm text-slate-100">{m.member_code}</span>
        <SideBadge side={m.placement_side} L={L} />
        <Chip className="border-slate-600 bg-slate-800 text-slate-400">{`G${m.generation ?? "—"}`}</Chip>
        <Chip className={riskCls}>{m.risk_state === "OK" ? "OK" : m.risk_state}</Chip>
        {m.placement_side_assumed ? <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-400">{L.assumedSide}</Chip> : null}
        {detail.sponsor_code ? <span className="text-xs text-slate-500">{L.sponsor(detail.sponsor_code)}</span> : null}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label={L.volLeft} value={pts(detail.volumes.left_volume)} />
        <Stat label={L.volRight} value={pts(detail.volumes.right_volume)} />
        <Stat label={L.carryLeft} value={pts(detail.volumes.left_carry)} />
        <Stat label={L.carryRight} value={pts(detail.volumes.right_carry)} />
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
        <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">{L.matching}</div>
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
          <div>min(L,R) = <span className="font-bold text-emerald-400">{`${pts(detail.matching.matched)} pts`}</span></div>
          <div>{L.leftConsumed(pts(detail.matching.left_consumed))}</div>
          <div>{L.rightConsumed(pts(detail.matching.right_consumed))}</div>
          <div>{L.carryCreated(pts(detail.matching.carry_created), detail.matching.carry_side)}</div>
          <div className="col-span-2">{L.bonusTarget5} <span className="font-bold text-blue-400">{money(detail.bonus.target)}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label={L.bonusPending} value={money(detail.bonus.pending)} />
        <Stat label={L.bonusAvailable} value={money(detail.bonus.available)} />
        <Stat label={L.totalGenerated} value={money(detail.bonus.total_generated)} />
      </div>
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">{L.binaryBonuses}</div>
        <BonusTable bonuses={bonuses} onWhy={onWhyBonus} L={L} />
      </div>
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">{L.volumePropagation}</div>
        <EventTable events={events} onWhy={onWhyVolume} L={L} />
      </div>
    </div>
  );
}

function SearchResults({ res, onSelect, onWhyVolume, L }) {
  return (
    <div className="space-y-1" data-testid="binary-search-results">
      {(res.members || []).map((m) => (
        <button key={m.member_id} onClick={() => onSelect(m.member_id)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-800/60">
          <SideBadge side={m.placement_side} L={L} />
          <span className="font-mono text-xs text-slate-200">{m.member_code}</span>
          <Chip className="border-slate-600 bg-slate-800 text-slate-400">{`G${m.generation ?? "—"}`}</Chip>
          {m.risk_state !== "OK" ? <Chip className={RISK_COLORS[m.risk_state]}>{m.risk_state}</Chip> : null}
        </button>
      ))}
      {(res.events || []).length > 0 ? (
        <div className="mt-2 border-t border-slate-800 pt-2">
          <div className="mb-1 text-[11px] uppercase text-slate-500">{L.volumeEvents}</div>
          {res.events.map((ev) => (
            <button key={ev.id} onClick={() => onWhyVolume(ev.id)} className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs hover:bg-slate-800/60">
              <SideBadge side={ev.branch} L={L} /><span className="font-mono">{`#${ev.order_number}`}</span>
              <span className="text-slate-400">{`${pts(ev.points)} pts`}</span>
              <Info className="ml-auto h-3.5 w-3.5 text-blue-400" />
            </button>
          ))}
        </div>
      ) : null}
      {!res.members?.length && !res.events?.length ? <div className="px-2 py-4 text-sm text-slate-600">{L.noResult}</div> : null}
    </div>
  );
}

function computeGraphLayout(rootId, nodes, kids, expanded) {
  const NODE_W = 168, NODE_H = 78, SLOT = 196, LEVEL_H = 132;
  if (!rootId || !nodes[rootId]) return { positioned: [], edges: [], width: 0, height: 0, NODE_W, NODE_H };
  const visible = [];
  const childrenOf = {};
  const queue = [{ id: rootId, depth: 0, side: null }];
  const seen = new Set();
  while (queue.length) {
    const cur = queue.shift();
    if (seen.has(cur.id)) continue;
    seen.add(cur.id);
    visible.push(cur);
    const k = kids[cur.id];
    const isOpen = expanded[cur.id] && k;
    childrenOf[cur.id] = { left: isOpen ? k.left : null, right: isOpen ? k.right : null };
    if (isOpen) {
      if (k.left && nodes[k.left]) queue.push({ id: k.left, depth: cur.depth + 1, side: "LEFT" });
      if (k.right && nodes[k.right]) queue.push({ id: k.right, depth: cur.depth + 1, side: "RIGHT" });
    }
  }
  const width = {};
  [...visible].sort((a, b) => b.depth - a.depth).forEach((v) => {
    const c = childrenOf[v.id];
    if (!c.left && !c.right) { width[v.id] = 1; return; }
    const lw = c.left ? (width[c.left] || 1) : 1;
    const rw = c.right ? (width[c.right] || 1) : 1;
    width[v.id] = lw + rw;
  });
  const center = {};
  const range = { [rootId]: [0, width[rootId]] };
  const q2 = [rootId];
  while (q2.length) {
    const id = q2.shift();
    const [lo, hi] = range[id];
    center[id] = (lo + hi) / 2;
    const c = childrenOf[id];
    const lw = c.left ? (width[c.left] || 1) : 1;
    if (c.left) { range[c.left] = [lo, lo + lw]; q2.push(c.left); }
    if (c.right) { range[c.right] = [lo + lw, hi]; q2.push(c.right); }
  }
  const positioned = visible.map((v) => ({
    id: v.id, node: nodes[v.id], depth: v.depth, side: v.side,
    x: center[v.id] * SLOT - NODE_W / 2, y: v.depth * LEVEL_H,
  }));
  const posById = {};
  positioned.forEach((p) => { posById[p.id] = p; });
  const edges = [];
  visible.forEach((v) => {
    const c = childrenOf[v.id];
    const parent = posById[v.id];
    ["left", "right"].forEach((sk) => {
      const cid = c[sk];
      if (cid && posById[cid]) {
        const child = posById[cid];
        edges.push({ x1: parent.x + NODE_W / 2, y1: parent.y + NODE_H,
          x2: child.x + NODE_W / 2, y2: child.y, side: sk.toUpperCase() });
      }
    });
  });
  const width_px = Math.max(NODE_W, ...positioned.map((p) => p.x + NODE_W)) + 40;
  const maxDepth = Math.max(0, ...visible.map((v) => v.depth));
  return { positioned, edges, width: width_px, height: (maxDepth + 1) * LEVEL_H, NODE_W, NODE_H };
}

function GraphNode({ p, active, expanded, onSelect, onToggle, nodeW, nodeH, L }) {
  const n = p.node;
  const canExpand = (n.child_count || 0) > 0;
  return (
    <div className="absolute" style={{ left: p.x, top: p.y, width: nodeW, height: nodeH }} data-testid={`binary-graph-node-${p.id}`}>
      <button onClick={onSelect}
        className={`flex h-full w-full flex-col justify-between rounded-lg border p-2 text-left transition-colors ${active ? "border-blue-500 bg-blue-600/20 ring-1 ring-blue-500/40" : "border-slate-700 bg-slate-900 hover:border-slate-500"}`}>
        <div className="flex items-center gap-1.5">
          <SideBadge side={n.placement_side} L={L} />
          <span className="truncate font-mono text-xs text-slate-100">{n.member_code}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Chip className="border-slate-600 bg-slate-800 text-slate-400">{`G${n.generation ?? "—"}`}</Chip>
          {n.risk_state !== "OK" && <Chip className={RISK_COLORS[n.risk_state]}>{n.risk_state}</Chip>}
          {n.placement_side_assumed && <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-400" title={L.assumedSideShort}>bf</Chip>}
        </div>
        <div className="text-[10px] text-slate-500">{`L ${pts(n.left_volume)} · R ${pts(n.right_volume)}`}</div>
      </button>
      {canExpand && (
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`binary-graph-toggle-${p.id}`}
          title={expanded ? L.reduce : L.expand}
          className="absolute -bottom-2.5 left-1/2 z-10 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full border border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700">
          {expanded ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

function BinaryGraphTree({ roots, selectedId, onSelect, L }) {
  const [rootId, setRootId] = useState(null);
  const [nodes, setNodes] = useState({});
  const [kids, setKids] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const map = {};
    roots.forEach((r) => { map[r.member_id] = r; });
    setNodes(map); setKids({}); setExpanded({});
    setRootId(roots[0]?.member_id || null);
  }, [roots]);

  const loadChildren = useCallback(async (id) => {
    try {
      const res = await api.get(`/admin/binary/tree/children/${id}`);
      const ch = res.data.children || {};
      setNodes((prev) => {
        const n = { ...prev };
        if (ch.left) n[ch.left.member_id] = ch.left;
        if (ch.right) n[ch.right.member_id] = ch.right;
        return n;
      });
      const entry = { left: ch.left?.member_id || null, right: ch.right?.member_id || null, loaded: true };
      setKids((prev) => ({ ...prev, [id]: entry }));
      return entry;
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); return null; }
  }, []);

  useEffect(() => {
    if (!rootId) return;
    const r = nodes[rootId];
    if (r && r.child_count > 0 && !expanded[rootId] && !kids[rootId]) {
      (async () => { await loadChildren(rootId); setExpanded((p) => ({ ...p, [rootId]: true })); })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootId, nodes]);

  const toggle = async (node) => {
    const id = node.member_id;
    if (expanded[id]) { setExpanded((p) => ({ ...p, [id]: false })); return; }
    if (node.child_count > 0 && !kids[id]) await loadChildren(id);
    setExpanded((p) => ({ ...p, [id]: true }));
  };

  const layout = useMemo(() => computeGraphLayout(rootId, nodes, kids, expanded), [rootId, nodes, kids, expanded]);

  return (
    <div className="space-y-2">
      {roots.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>{L.rootLabel}</span>
          <select data-testid="binary-graph-root-select" value={rootId || ""} onChange={(e) => setRootId(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-slate-200 outline-none">
            {roots.map((r) => <option key={r.member_id} value={r.member_id}>{r.member_code}</option>)}
          </select>
        </div>
      )}
      <div className="relative max-h-[620px] overflow-auto rounded-lg border border-slate-800 bg-slate-950/40" data-testid="binary-graph-tree">
        <div className="relative" style={{ width: layout.width, height: layout.height, minWidth: "100%" }}>
          <svg className="pointer-events-none absolute inset-0" width={layout.width} height={layout.height}>
            {layout.edges.map((e, i) => (
              <path key={i} d={`M ${e.x1} ${e.y1} C ${e.x1} ${(e.y1 + e.y2) / 2}, ${e.x2} ${(e.y1 + e.y2) / 2}, ${e.x2} ${e.y2}`}
                stroke={e.side === "LEFT" ? "#0ea5e9" : "#8b5cf6"} strokeWidth="1.5" fill="none" opacity="0.55" />
            ))}
          </svg>
          {layout.positioned.map((p) => (
            <GraphNode key={p.id} p={p} active={selectedId === p.id} expanded={!!expanded[p.id]}
              onSelect={() => onSelect(p.id)} onToggle={() => toggle(p.node)} nodeW={layout.NODE_W} nodeH={layout.NODE_H} L={L} />
          ))}
          {!layout.positioned.length && <div className="p-6 text-sm text-slate-600">{L.noNode}</div>}
        </div>
      </div>
      {rootId && (nodes[rootId]?.child_count || 0) === 0 && layout.positioned.length === 1 && (
        <div className="rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2 text-[11px] text-slate-500" data-testid="binary-graph-empty-hint">
          {L.rootNoChild}
        </div>
      )}
    </div>
  );
}

export default function AdminBinaryAudit() {
  const L = useT();
  const [roots, setRoots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [searchRes, setSearchRes] = useState(null);
  const [explain, setExplain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list | graph

  const loadRoots = useCallback(async () => {
    try {
      const res = await api.get("/admin/binary/tree/roots", { params: { limit: 100 } });
      setRoots(res.data.roots || []);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadRoots(); }, [loadRoots]);

  const select = async (mid) => {
    setSelected(mid); setLoading(true); setSearchRes(null);
    try {
      const [d, b, ev] = await Promise.all([
        api.get(`/admin/binary/members/${mid}`),
        api.get(`/admin/binary/members/${mid}/bonuses`),
        api.get(`/admin/binary/members/${mid}/volume-events`, { params: { limit: 50 } }),
      ]);
      setDetail(d.data); setBonuses(b.data.bonuses || []); setEvents(ev.data.events || []);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setLoading(false);
  };

  const runSearch = async () => {
    if (!q.trim()) { setSearchRes(null); return; }
    try {
      const res = await api.get("/admin/binary/search", { params: { q: q.trim() } });
      setSearchRes(res.data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const whyBonus = async (id) => {
    try { const res = await api.get(`/admin/binary/bonus/${id}/explain`); setExplain({ kind: "bonus", payload: res.data }); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  const whyVolume = async (id) => {
    try { const res = await api.get(`/admin/binary/volume-event/${id}/explain`); setExplain({ kind: "volume", payload: res.data }); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="binary-audit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-slate-100"><Network className="h-5 w-5 text-blue-400" /> {L.pageTitle}</h1>
          <p className="text-sm text-slate-500">{L.pageSub}</p>
        </div>
        <button onClick={loadRoots} data-testid="binary-refresh-btn" className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
          <RefreshCw className="h-3.5 w-3.5" /> {L.refresh}
        </button>
      </div>

      <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900/60 p-0.5" data-testid="binary-view-toggle">
        <button data-testid="binary-view-list" onClick={() => setViewMode("list")}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === "list" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>
          <List className="h-4 w-4" /> {L.list}
        </button>
        <button data-testid="binary-view-graph" onClick={() => setViewMode("graph")}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === "graph" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>
          <GitBranch className="h-4 w-4" /> {L.graphTree}
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
        <Search className="h-4 w-4 text-slate-500" />
        <input data-testid="binary-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
          placeholder={L.searchPh} className="flex-1 bg-transparent text-sm text-slate-200 outline-none placeholder:text-slate-600" />
        <button onClick={runSearch} data-testid="binary-search-btn" className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-500">{L.searchBtn}</button>
      </div>

      {viewMode === "graph" && !searchRes ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{L.graphTitle}</div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500" />LEFT</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-violet-500" />RIGHT</span>
                <span className="text-slate-600">{L.expandHint}</span>
              </div>
            </div>
            {!roots.length ? <div className="px-2 py-6 text-sm text-slate-600">{L.noRoot}</div>
              : <BinaryGraphTree roots={roots} selectedId={selected} onSelect={select} L={L} />}
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            {!detail ? (
              <div className="flex min-h-[160px] items-center justify-center text-sm text-slate-600">{L.clickNode}</div>
            ) : loading ? (
              <div className="py-10 text-center text-sm text-slate-500">{L.loading}</div>
            ) : (
              <MemberDetail detail={detail} bonuses={bonuses} events={events} onWhyBonus={whyBonus} onWhyVolume={whyVolume} L={L} />
            )}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{searchRes ? L.searchResults : L.treeRoots}</div>
          {searchRes ? (
            <SearchResults res={searchRes} onSelect={select} onWhyVolume={whyVolume} L={L} />
          ) : (
            <div className="max-h-[560px] space-y-0.5 overflow-auto">
              <TreeExplorer roots={roots} selectedId={selected} onSelect={select} L={L} />
              {!roots.length ? <div className="px-2 py-4 text-sm text-slate-600">{L.noRoot}</div> : null}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          {!detail ? (
            <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-600">{L.selectMember}</div>
          ) : loading ? (
            <div className="py-10 text-center text-sm text-slate-500">{L.loading}</div>
          ) : (
            <MemberDetail detail={detail} bonuses={bonuses} events={events} onWhyBonus={whyBonus} onWhyVolume={whyVolume} L={L} />
          )}
        </div>
      </div>
      )}

      <ExplainModal explain={explain} onClose={() => setExplain(null)} L={L} />
    </div>
  );
}
